'use client';

import { FC, memo, useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTranslations } from 'next-intl';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { OfferType, Unit } from '@/types';
import { getUnitList } from '@/common';
import { WlasLocationPicker } from './WlasLocationPicker';
import { COLORS, MAX_FILTERS_RANGE_VALUES } from '@/constants';
import { WlasFilterOptions } from '@/queris/useGetWlasFilterOptions';
import {
  Card,
  CardCollapseBody,
  CardSubtitle,
  CardTitle,
  CardTitleHeader,
  CollapseToggle,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/styled';
import { WlasChevronIcon } from '../WlasParamsCard/components/WlasSection/WlasChevronIcon';
import { WlasFilters } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/utils';
import { LocationWrapper, OfferTypeList, PriceRangeStack } from './styled';
import { WlasFiltersFooter } from '../WlasFiltersFooter';
import { FilterSection } from './FilterSection';
import { CheckboxList } from './CheckboxList';
import { RangeInputs } from './RangeInputs';

interface WlasDataFiltersCardProps {
  filters: WlasFilters;
  filterOptions: WlasFilterOptions | undefined;
  onChange: <K extends keyof WlasFilters>(key: K, value: WlasFilters[K]) => void;
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onResetDefaults: () => void;
}

export const WlasDataFiltersCard: FC<WlasDataFiltersCardProps> = memo(
  ({ filters, filterOptions, onChange, isDirty, isSaving, onSave, onResetDefaults }) => {
    const t = useTranslations('wlasWidget.filters');
    const tHint = useTranslations('searchResult.hints');
    const [open, setOpen] = useLocalStorage('wlas-card-filters-open', true);

    const toggleArrayItem = useCallback(
      <K extends keyof WlasFilters>(key: K, code: string, checked: boolean, allCodes: string[]) => {
        const current = filters[key] as string[] | null;
        // null = no restriction (all unchecked in UI); treat as empty selection
        const base = current ?? [];
        const next = checked ? [...base, code] : base.filter((c) => c !== code);
        // if all items are checked, collapse back to null (= no restriction)
        const value = next.length === allCodes.length ? null : next;
        onChange(key, value as WlasFilters[K]);
      },
      [filters, onChange],
    );

    const unitList = filterOptions
      ? getUnitList(
          filterOptions.countries as Unit[],
          filterOptions.federalStates as Unit[],
          filterOptions.districts as Unit[],
        )
      : undefined;

    return (
      <Card>
        <CardTitleHeader onClick={() => setOpen((v) => !v)}>
          <div>
            <CardTitle>{t('title')}</CardTitle>
            <CardSubtitle>{t('subtitle')}</CardSubtitle>
          </div>
          <CollapseToggle
            type="button"
            aria-expanded={open}
            aria-controls="wlas-filters-body"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            <WlasChevronIcon open={open} />
          </CollapseToggle>
        </CardTitleHeader>
        {open && (
          <CardCollapseBody>
            <div id="wlas-filters-body">
              {/* 1. Angebotstyp */}
              <FilterSection
                storageKey="wlas-filter-offerType"
                title={t('sections.offerType')}
                isFirst
              >
                <OfferTypeList>
                  {[
                    { label: 'Kauf', value: OfferType.SELL },
                    { label: 'Miete', value: OfferType.RENT },
                  ].map(({ label, value }) => (
                    <FormControlLabel
                      key={value}
                      label={
                        <Typography fontSize={14} color={COLORS['primary/900']}>
                          {label}
                        </Typography>
                      }
                      control={
                        <Checkbox
                          size="small"
                          checked={
                            filters.offerTypes !== null && filters.offerTypes.includes(value)
                          }
                          onChange={(e) =>
                            toggleArrayItem(
                              'offerTypes',
                              value,
                              e.target.checked,
                              Object.values(OfferType),
                            )
                          }
                        />
                      }
                    />
                  ))}
                </OfferTypeList>
              </FilterSection>

              {/* 2. Immobilienart */}
              {filterOptions?.categories && filterOptions.categories.length > 0 && (
                <FilterSection storageKey="wlas-filter-categories" title={t('sections.categories')}>
                  <CheckboxList
                    grid
                    items={filterOptions.categories}
                    selected={filters.categories}
                    onToggle={(code, checked) =>
                      toggleArrayItem(
                        'categories',
                        code,
                        checked,
                        filterOptions.categories.map((c) => c.code),
                      )
                    }
                  />
                </FilterSection>
              )}

              {/* 3. Land / Bundesland / Bezirk */}
              <FilterSection storageKey="wlas-filter-location" title={t('sections.location')}>
                <LocationWrapper>
                  <WlasLocationPicker
                    unitList={unitList}
                    value={filters.unitListValue}
                    onChange={(value) => onChange('unitListValue', value)}
                  />
                </LocationWrapper>
              </FilterSection>

              {/* 4. Ausstattung */}
              {filterOptions?.equipment && filterOptions.equipment.length > 0 && (
                <FilterSection storageKey="wlas-filter-equipment" title={t('sections.equipment')}>
                  <CheckboxList
                    grid
                    items={filterOptions.equipment}
                    selected={filters.equipment}
                    onToggle={(code, checked) =>
                      toggleArrayItem(
                        'equipment',
                        code,
                        checked,
                        filterOptions.equipment.map((e) => e.code),
                      )
                    }
                  />
                </FilterSection>
              )}

              {/* 5. Fläche */}
              <FilterSection
                storageKey="wlas-filter-area"
                title={t('sections.area')}
                hint={tHint('maxValue')}
              >
                <RangeInputs
                  minLabel={t('areaLabels.min')}
                  maxLabel={t('areaLabels.max')}
                  value={filters.areaValue}
                  minValue={0}
                  maxValue={MAX_FILTERS_RANGE_VALUES.AREA}
                  onChange={(v) => onChange('areaValue', v)}
                />
              </FilterSection>

              {/* 6. Zimmer */}
              <FilterSection
                storageKey="wlas-filter-rooms"
                title={t('sections.rooms')}
                hint={tHint('maxValue')}
              >
                <RangeInputs
                  minLabel={t('roomsLabels.min')}
                  maxLabel={t('roomsLabels.max')}
                  value={filters.roomsValue}
                  minValue={0}
                  maxValue={MAX_FILTERS_RANGE_VALUES.ROOMS}
                  onChange={(v) => onChange('roomsValue', v)}
                />
              </FilterSection>

              {/* 7. Preis */}
              <FilterSection
                storageKey="wlas-filter-price"
                title={t('sections.price')}
                hint={tHint('maxValue')}
              >
                <PriceRangeStack>
                  <RangeInputs
                    minLabel={t('priceLabels.buyMin')}
                    maxLabel={t('priceLabels.buyMax')}
                    value={filters.priceValue}
                    minValue={0}
                    maxValue={MAX_FILTERS_RANGE_VALUES.SELL_PRICE}
                    onChange={(v) => onChange('priceValue', v)}
                  />
                  <RangeInputs
                    minLabel={t('priceLabels.rentMin')}
                    maxLabel={t('priceLabels.rentMax')}
                    value={filters.rentPriceValue}
                    minValue={0}
                    maxValue={MAX_FILTERS_RANGE_VALUES.RENT_PRICE}
                    onChange={(v) => onChange('rentPriceValue', v)}
                  />
                </PriceRangeStack>
              </FilterSection>
            </div>
            <WlasFiltersFooter
              isDirty={isDirty}
              isSaving={isSaving}
              isInvalid={false}
              onSave={onSave}
              onResetDefaults={onResetDefaults}
            />
          </CardCollapseBody>
        )}
      </Card>
    );
  },
);
WlasDataFiltersCard.displayName = 'WlasDataFiltersCard';
